import { zodResolver } from '@hookform/resolvers/zod';
import { BookmarkPlus, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Controller, useForm, type UseFormReturn } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
  createIntentKey,
  validateCreationStyleTag,
  useCreateCreationPresetMutation,
  useCreateGenerationMutation,
  useCreationCapabilitiesQuery,
  useCreationPresetsQuery,
  useProjectsQuery,
  type CreateGenerationBody,
  type CreationCapabilities,
  type CreationPreset,
  type Project,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  InfoTooltip,
  Input,
  Panel,
  Select,
  StateSurface,
  Textarea,
} from '../../design-system/index.js';
import { cn } from '../../design-system/cn.js';
import { PageHeader, SegmentedControl } from '../shared/page.js';
import {
  createDraftScope,
  readCreateDraft,
  stageCreateDraft,
  writeCreateDraft,
} from './create-draft.js';
import { StyleTagInput } from './style-tag-input.js';

const PUBLIC_PROMPT_MAX = 10_000;
const PUBLIC_LYRICS_MAX = 100_000;

type GenerationInput = CreateGenerationBody['input'];
type AdvancedGenerationInput = Extract<GenerationInput, { mode: 'advanced' }>;
type ModelId = AdvancedGenerationInput['model_id'];
type Mode = CreationCapabilities['modes'][number];
type NumericRange = NonNullable<
  CreationCapabilities['controls']['advanced_models']['v3']['style_strength']['range']
>;

const formFieldsSchema = z.object({
  presetId: z.string(),
  mode: z.enum(['simple', 'advanced']),
  modelId: z.enum(['v2.2', 'v3']),
  simpleModelId: z.enum(['v2.2', 'v3']),
  simplePrompt: z.string().max(PUBLIC_PROMPT_MAX),
  styleGuidanceMode: z.enum(['auto', 'tags']),
  styleInstruction: z.string().max(PUBLIC_PROMPT_MAX),
  styleTags: z.array(z.string()),
  avoidanceEnabled: z.boolean(),
  avoidanceTags: z.array(z.string()),
  lyricsMode: z.enum(['auto', 'custom', 'none']),
  lyricsInstruction: z.string().max(PUBLIC_PROMPT_MAX),
  customLyrics: z.string().max(PUBLIC_LYRICS_MAX),
  styleStrength: z.string(),
  expandedStrength: z.boolean(),
  durationEnabled: z.boolean(),
  durationMinimum: z.string(),
  durationMaximum: z.string(),
  fancyMode: z.boolean(),
  moreAdvanced: z.boolean(),
  lyricsStyleStrength: z.string(),
  balance: z.string(),
  bpmMode: z.enum(['disabled', 'auto', 'manual']),
  beatsPerMinute: z.string(),
  boostStyle: z.boolean(),
});

export type CreationFields = z.infer<typeof formFieldsSchema>;

export function parseCreationFields(value: unknown): CreationFields | null {
  const parsed = formFieldsSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function minimumStyleTagCount(
  capabilities: CreationCapabilities,
  modelId: CreationFields['modelId'],
) {
  return capabilities.controls.advanced_models[modelId].style_guidance.min_items;
}

function formSchema(capabilities: CreationCapabilities) {
  return formFieldsSchema.superRefine((values, context) => {
    const controls = capabilities.controls;
    if (values.mode === 'simple') {
      if (!values.simplePrompt.trim()) {
        context.addIssue({
          code: 'custom',
          message: 'Enter a creation prompt.',
          path: ['simplePrompt'],
        });
      }
      return;
    }

    if (values.styleGuidanceMode === 'auto' && !values.styleInstruction.trim()) {
      context.addIssue({
        code: 'custom',
        message: 'Describe the sound.',
        path: ['styleInstruction'],
      });
    }
    if (values.styleGuidanceMode === 'tags') {
      const minimum = minimumStyleTagCount(capabilities, values.modelId);
      if (values.styleTags.length < minimum) {
        context.addIssue({
          code: 'custom',
          message: `Enter at least ${minimum} style tags.`,
          path: ['styleTags'],
        });
      }
      const maximum = controls.style_guidance.max_items;
      if (maximum !== null && values.styleTags.length > maximum) {
        context.addIssue({
          code: 'custom',
          message: `Use no more than ${maximum} style tags.`,
          path: ['styleTags'],
        });
      }
    }
    if (values.modelId === 'v3' && values.avoidanceEnabled) {
      const minimum = 1;
      if (values.avoidanceTags.length < minimum) {
        context.addIssue({
          code: 'custom',
          message: 'Select at least one style to avoid.',
          path: ['avoidanceTags'],
        });
      }
    }
    if (values.lyricsMode === 'auto' && !values.lyricsInstruction.trim()) {
      context.addIssue({
        code: 'custom',
        message: 'Describe what the lyrics should be about.',
        path: ['lyricsInstruction'],
      });
    }
    if (values.lyricsMode === 'custom' && !values.customLyrics.trim()) {
      context.addIssue({ code: 'custom', message: 'Enter the lyrics.', path: ['customLyrics'] });
    }

    if (values.modelId === 'v3') {
      const v3 = controls.advanced_models.v3;
      const strength = toNumber(values.styleStrength);
      const strengthRange = values.expandedStrength
        ? (v3.style_strength.expanded_range ?? v3.style_strength.range)
        : v3.style_strength.range;
      if (strength === undefined) {
        context.addIssue({
          code: 'custom',
          message: 'Enter a style strength.',
          path: ['styleStrength'],
        });
      } else if (strengthRange) {
        validateNumberAgainstBounds(
          strength,
          'styleStrength',
          'Style strength',
          strengthRange,
          context,
        );
      }
    }

    if (values.modelId === 'v3' && values.durationEnabled) {
      const duration = controls.advanced_models.v3.duration;
      const minimum = toNumber(values.durationMinimum);
      const maximum = toNumber(values.durationMaximum);
      if (minimum === undefined) {
        context.addIssue({
          code: 'custom',
          message: 'Enter a minimum duration.',
          path: ['durationMinimum'],
        });
      }
      if (maximum === undefined) {
        context.addIssue({
          code: 'custom',
          message: 'Enter a maximum duration.',
          path: ['durationMaximum'],
        });
      }
      if (minimum !== undefined && maximum !== undefined) {
        if (minimum > maximum) {
          context.addIssue({
            code: 'custom',
            message: 'Maximum must be greater than or equal to minimum.',
            path: ['durationMaximum'],
          });
        }
        if (duration.range) {
          validateNumberAgainstBounds(
            minimum,
            'durationMinimum',
            'Minimum duration',
            duration.range,
            context,
          );
          validateNumberAgainstBounds(
            maximum,
            'durationMaximum',
            'Maximum duration',
            duration.range,
            context,
          );
        }
      }
    }

    if (values.modelId === 'v2.2') {
      const v22 = controls.advanced_models['v2.2'];
      const lyricsStyleStrength = toNumber(values.lyricsStyleStrength);
      const balance = toNumber(values.balance);
      if (lyricsStyleStrength === undefined) {
        context.addIssue({
          code: 'custom',
          message: 'Enter a lyrics and style strength.',
          path: ['lyricsStyleStrength'],
        });
      } else if (v22.lyrics_style_strength.range) {
        validateNumberAgainstBounds(
          lyricsStyleStrength,
          'lyricsStyleStrength',
          'Lyrics and style strength',
          v22.lyrics_style_strength.range,
          context,
        );
      }
      if (balance === undefined) {
        context.addIssue({ code: 'custom', message: 'Enter a balance.', path: ['balance'] });
      } else if (v22.balance.range) {
        validateNumberAgainstBounds(balance, 'balance', 'Balance', v22.balance.range, context);
      }
      if (values.bpmMode === 'manual') {
        const bpm = toNumber(values.beatsPerMinute);
        if (bpm === undefined) {
          context.addIssue({ code: 'custom', message: 'Enter a BPM.', path: ['beatsPerMinute'] });
        } else if (v22.bpm.manual_range) {
          validateNumberAgainstBounds(bpm, 'beatsPerMinute', 'BPM', v22.bpm.manual_range, context);
        }
      }
    }
  });
}

export function validateCreationFields(
  value: unknown,
  capabilities: CreationCapabilities,
): CreationFields | null {
  const parsed = formSchema(capabilities).safeParse(value);
  return parsed.success ? parsed.data : null;
}

function validateNumberAgainstBounds(
  value: number,
  path:
    | 'styleStrength'
    | 'durationMinimum'
    | 'durationMaximum'
    | 'lyricsStyleStrength'
    | 'balance'
    | 'beatsPerMinute',
  label: string,
  bounds: NumericRange,
  context: z.RefinementCtx,
) {
  if (value < bounds.minimum || value > bounds.maximum) {
    context.addIssue({
      code: 'custom',
      message: `${label} must be between ${bounds.minimum} and ${bounds.maximum}.`,
      path: [path],
    });
    return;
  }
  if (bounds.step !== null) {
    const intervals = (value - bounds.minimum) / bounds.step;
    if (Math.abs(intervals - Math.round(intervals)) >= 1e-7) {
      context.addIssue({
        code: 'custom',
        message: `${label} must use increments of ${bounds.step}.`,
        path: [path],
      });
    }
  }
}

function toNumber(value: string) {
  const number = Number(value);
  return value.trim() === '' || !Number.isFinite(number) ? undefined : number;
}

function isModelId(value: unknown): value is ModelId {
  return value === 'v2.2' || value === 'v3';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

const emptyValues: CreationFields = {
  presetId: '',
  mode: 'advanced',
  modelId: 'v3',
  simpleModelId: 'v3',
  simplePrompt: '',
  styleGuidanceMode: 'auto',
  styleInstruction: '',
  styleTags: [],
  avoidanceEnabled: false,
  avoidanceTags: [],
  lyricsMode: 'auto',
  lyricsInstruction: '',
  customLyrics: '',
  styleStrength: '4.5',
  expandedStrength: false,
  durationEnabled: false,
  durationMinimum: '0',
  durationMaximum: '300',
  fancyMode: false,
  moreAdvanced: false,
  lyricsStyleStrength: '1.85',
  balance: '1',
  bpmMode: 'manual',
  beatsPerMinute: '120',
  boostStyle: false,
};

function valuesFromPreset(
  preset: CreationPreset | undefined,
  capabilities: CreationCapabilities,
  requestedMode: Mode,
): CreationFields {
  const defaults: Record<string, unknown> = isRecord(preset?.input_defaults)
    ? preset.input_defaults
    : {};
  const styleGuidance = isRecord(defaults.style_guidance) ? defaults.style_guidance : undefined;
  const avoidance = isRecord(defaults.avoidance) ? defaults.avoidance : undefined;
  const lyrics = isRecord(defaults.lyrics) ? defaults.lyrics : undefined;
  const duration = isRecord(defaults.duration) ? defaults.duration : undefined;
  const modelId = isModelId(defaults.model_id)
    ? defaults.model_id
    : isModelId(capabilities.controls.model.default_id)
      ? capabilities.controls.model.default_id
      : 'v3';
  const v3 = capabilities.controls.advanced_models.v3;
  const v22 = capabilities.controls.advanced_models['v2.2'];
  const styleStrength =
    typeof defaults.style_strength === 'number'
      ? defaults.style_strength
      : (v3.style_strength.default ?? 4.5);

  return {
    ...emptyValues,
    presetId: preset?.id ?? '',
    mode: preset?.mode ?? requestedMode,
    modelId,
    simpleModelId:
      preset?.mode === 'simple' && isModelId(defaults.model_id)
        ? defaults.model_id
        : isModelId(capabilities.controls.model.default_id)
          ? capabilities.controls.model.default_id
          : 'v3',
    simplePrompt: typeof defaults.prompt === 'string' ? defaults.prompt : '',
    styleGuidanceMode: styleGuidance?.mode === 'tags' ? 'tags' : 'auto',
    styleInstruction:
      styleGuidance?.mode === 'auto' && typeof styleGuidance.instruction === 'string'
        ? styleGuidance.instruction
        : '',
    styleTags: styleGuidance?.mode === 'tags' ? stringList(styleGuidance.tags) : [],
    avoidanceEnabled: avoidance?.mode === 'tags',
    avoidanceTags: avoidance?.mode === 'tags' ? stringList(avoidance.tags) : [],
    lyricsMode:
      lyrics?.mode === 'custom' || lyrics?.mode === 'none' || lyrics?.mode === 'auto'
        ? lyrics.mode
        : 'auto',
    lyricsInstruction:
      lyrics?.mode === 'auto' && typeof lyrics.instruction === 'string' ? lyrics.instruction : '',
    customLyrics: lyrics?.mode === 'custom' && typeof lyrics.text === 'string' ? lyrics.text : '',
    styleStrength: String(styleStrength),
    durationEnabled: duration?.mode === 'range',
    durationMinimum:
      duration?.mode === 'range' && typeof duration.minimum_seconds === 'number'
        ? String(duration.minimum_seconds)
        : String(v3.duration.range?.minimum ?? 0),
    durationMaximum:
      duration?.mode === 'range' && typeof duration.maximum_seconds === 'number'
        ? String(duration.maximum_seconds)
        : String(v3.duration.range?.maximum ?? 300),
    fancyMode:
      typeof defaults.fancy_mode === 'boolean'
        ? defaults.fancy_mode
        : (v22.fancy_mode.default ?? false),
    lyricsStyleStrength: String(
      typeof defaults.lyrics_style_strength === 'number'
        ? defaults.lyrics_style_strength
        : (v22.lyrics_style_strength.default ?? 1.85),
    ),
    balance: String(
      typeof defaults.balance === 'number' ? defaults.balance : (v22.balance.default ?? 1),
    ),
    bpmMode:
      isRecord(defaults.bpm) &&
      (defaults.bpm.mode === 'disabled' ||
        defaults.bpm.mode === 'auto' ||
        defaults.bpm.mode === 'manual')
        ? defaults.bpm.mode
        : v22.bpm.default.mode,
    beatsPerMinute:
      isRecord(defaults.bpm) &&
      defaults.bpm.mode === 'manual' &&
      typeof defaults.bpm.beats_per_minute === 'number'
        ? String(defaults.bpm.beats_per_minute)
        : v22.bpm.default.mode === 'manual'
          ? String(v22.bpm.default.beats_per_minute)
          : '120',
    boostStyle:
      typeof defaults.boost_style === 'boolean'
        ? defaults.boost_style
        : (v22.boost_style.default ?? false),
  };
}

export function buildCreateGenerationRequest(
  values: CreationFields,
  connectionId: string,
  composerProjectId: string | null,
): CreateGenerationBody {
  if (!connectionId) throw new Error('A provider connection is required.');

  return {
    contract_version: 2,
    connection_id: connectionId,
    ...(composerProjectId ? { composer_project_id: composerProjectId } : {}),
    ...(values.presetId ? { preset_id: values.presetId } : {}),
    input: buildGenerationInput(values),
  } satisfies CreateGenerationBody;
}

export function buildGenerationInput(values: CreationFields): GenerationInput {
  const styleGuidance =
    values.styleGuidanceMode === 'auto'
      ? { mode: 'auto' as const, instruction: values.styleInstruction.trim() }
      : { mode: 'tags' as const, tags: values.styleTags };
  const lyrics =
    values.lyricsMode === 'auto'
      ? { mode: 'auto' as const, instruction: values.lyricsInstruction.trim() }
      : values.lyricsMode === 'custom'
        ? { mode: 'custom' as const, text: values.customLyrics.trim() }
        : { mode: 'none' as const };
  const input =
    values.mode === 'simple'
      ? {
          mode: 'simple' as const,
          prompt: values.simplePrompt.trim(),
          model_id: values.simpleModelId,
        }
      : values.modelId === 'v2.2'
        ? {
            mode: 'advanced' as const,
            model_id: 'v2.2' as const,
            style_guidance: styleGuidance,
            lyrics,
            fancy_mode: values.fancyMode,
            lyrics_style_strength: toNumber(values.lyricsStyleStrength) ?? 1.85,
            balance: toNumber(values.balance) ?? 1,
            bpm:
              values.bpmMode === 'manual'
                ? {
                    mode: 'manual' as const,
                    beats_per_minute: toNumber(values.beatsPerMinute) ?? 120,
                  }
                : values.bpmMode === 'auto'
                  ? { mode: 'auto' as const }
                  : { mode: 'disabled' as const },
            boost_style: values.boostStyle,
          }
        : {
            mode: 'advanced' as const,
            model_id: 'v3' as const,
            style_guidance: styleGuidance,
            avoidance: values.avoidanceEnabled
              ? { mode: 'tags' as const, tags: values.avoidanceTags }
              : { mode: 'off' as const },
            lyrics,
            style_strength: toNumber(values.styleStrength) ?? 4.5,
            duration: values.durationEnabled
              ? {
                  mode: 'range' as const,
                  minimum_seconds: toNumber(values.durationMinimum) ?? 0,
                  maximum_seconds: toNumber(values.durationMaximum) ?? 300,
                }
              : { mode: 'provider_default' as const },
          };

  return input;
}

export function CreatePage() {
  const { connection, library } = useWorkspace();
  const capabilities = useCreationCapabilitiesQuery(connection.id);
  const presets = useCreationPresetsQuery(connection.id);
  const projects = useProjectsQuery({ status: 'active', limit: 100 });
  const [searchParams] = useSearchParams();
  const draftScope = createDraftScope(connection.id, library.id);
  const storedDraft = useMemo(() => readCreateDraft(draftScope, parseCreationFields), [draftScope]);

  if (capabilities.isLoading || presets.isLoading || projects.isLoading) {
    return <StateSurface kind="loading" title="Loading creation options" />;
  }
  if (capabilities.error || presets.error || projects.error) {
    return (
      <StateSurface
        kind="error"
        title="Song creation is unavailable"
        action={
          <Button
            variant="secondary"
            onClick={() =>
              void Promise.all([capabilities.refetch(), presets.refetch(), projects.refetch()])
            }
          >
            Try again
          </Button>
        }
      />
    );
  }
  if (!capabilities.data) {
    return <StateSurface title="Song creation is unavailable" />;
  }

  const activeProjects =
    projects.data?.items.filter((project) => project.status === 'active') ?? [];
  const requestedProjectId = searchParams.get('project') ?? storedDraft?.projectId ?? null;
  const initialProjectId = activeProjects.some((project) => project.id === requestedProjectId)
    ? requestedProjectId
    : null;
  const initialMode = capabilities.data.modes[0] ?? 'advanced';
  const defaultPreset =
    presets.data?.items.find((item) => item.mode === initialMode && item.is_default) ??
    presets.data?.items.find((item) => item.mode === initialMode);
  const initialFields =
    storedDraft?.fields ?? valuesFromPreset(defaultPreset, capabilities.data, initialMode);
  const fallbackModelId: ModelId = isModelId(capabilities.data.controls.model.default_id)
    ? capabilities.data.controls.model.default_id
    : (capabilities.data.controls.model.options.map((option) => option.id).find(isModelId) ?? 'v3');
  const reconciledFields: CreationFields = {
    ...initialFields,
    mode: capabilities.data.modes.includes(initialFields.mode) ? initialFields.mode : initialMode,
    modelId: capabilities.data.controls.model.options.some(
      (option) => option.id === initialFields.modelId,
    )
      ? initialFields.modelId
      : fallbackModelId,
    simpleModelId: capabilities.data.controls.model.options.some(
      (option) => option.id === initialFields.simpleModelId,
    )
      ? initialFields.simpleModelId
      : fallbackModelId,
    presetId: (presets.data?.items ?? []).some(
      (preset) => preset.id === initialFields.presetId && preset.mode === initialFields.mode,
    )
      ? initialFields.presetId
      : '',
  };

  return (
    <CreateForm
      key={draftScope}
      capability={capabilities.data}
      connection={connection}
      draftScope={draftScope}
      presets={presets.data?.items ?? []}
      projects={activeProjects}
      initialFields={reconciledFields}
      initialProjectId={initialProjectId}
    />
  );
}

export function CreateForm({
  capability,
  connection,
  draftScope,
  initialFields,
  presets,
  projects,
  initialProjectId,
}: {
  capability: CreationCapabilities;
  connection: ReturnType<typeof useWorkspace>['connection'];
  draftScope?: string;
  initialFields?: CreationFields;
  presets: CreationPreset[];
  projects: Project[];
  initialProjectId: string | null;
}) {
  const create = useCreateGenerationMutation();
  const savePreset = useCreateCreationPresetMutation();
  const [presetDraft, setPresetDraft] = useState<GenerationInput | null>(null);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [savedPresetName, setSavedPresetName] = useState<string | null>(null);
  const [projectId, setProjectId] = useState(initialProjectId);
  const initialMode = capability.modes[0] ?? 'advanced';
  const defaultPreset =
    presets.find((item) => item.mode === initialMode && item.is_default) ??
    presets.find((item) => item.mode === initialMode);
  const form = useForm<CreationFields>({
    defaultValues: initialFields ?? valuesFromPreset(defaultPreset, capability, initialMode),
    resolver: zodResolver(formSchema(capability)),
  });
  const projectIdRef = useRef(projectId);
  const draftWriteRef = useRef<number | null>(null);

  useEffect(() => {
    const currentDraft = () => {
      const parsed = formFieldsSchema.safeParse(form.getValues());
      return parsed.success ? { fields: parsed.data, projectId: projectIdRef.current } : null;
    };
    const persist = () => {
      const draft = currentDraft();
      if (draftScope && draft) writeCreateDraft(draftScope, draft);
    };
    const schedule = () => {
      const draft = currentDraft();
      if (draftScope && draft) stageCreateDraft(draftScope, draft);
      if (draftWriteRef.current !== null) window.clearTimeout(draftWriteRef.current);
      draftWriteRef.current = window.setTimeout(() => {
        draftWriteRef.current = null;
        persist();
      }, 250);
    };
    if (!draftScope) return;
    const subscription = form.watch(schedule);
    const onPageHide = () => persist();
    window.addEventListener('pagehide', onPageHide);
    return () => {
      subscription.unsubscribe();
      window.removeEventListener('pagehide', onPageHide);
      if (draftWriteRef.current !== null) window.clearTimeout(draftWriteRef.current);
      persist();
    };
  }, [draftScope, form]);

  useEffect(() => {
    projectIdRef.current = projectId;
    const parsed = formFieldsSchema.safeParse(form.getValues());
    if (draftScope && parsed.success) {
      writeCreateDraft(draftScope, { fields: parsed.data, projectId });
    }
  }, [draftScope, form, projectId]);
  const controls = capability.controls;
  const mode = form.watch('mode');
  const modelId = form.watch('modelId');
  const lyricsMode = form.watch('lyricsMode');
  const styleGuidanceMode = form.watch('styleGuidanceMode');
  const avoidanceEnabled = form.watch('avoidanceEnabled');
  const expandedStrength = form.watch('expandedStrength');
  const durationEnabled = form.watch('durationEnabled');
  const moreAdvanced = form.watch('moreAdvanced');
  const bpmMode = form.watch('bpmMode');
  const simplePrompt = form.watch('simplePrompt');
  const lyricsInstruction = form.watch('lyricsInstruction');
  const customLyrics = form.watch('customLyrics');
  const styleInstruction = form.watch('styleInstruction');
  const v3Controls = controls.advanced_models.v3;
  const v22Controls = controls.advanced_models['v2.2'];
  const simplePromptMax = controls.simple_prompt.max_characters ?? PUBLIC_PROMPT_MAX;
  const lyricsInstructionMax = controls.lyrics.auto_instruction_max_characters ?? PUBLIC_PROMPT_MAX;
  const customLyricsMax = controls.lyrics.custom_text_max_characters ?? PUBLIC_LYRICS_MAX;
  const styleInstructionMax = controls.style_guidance.max_characters ?? PUBLIC_PROMPT_MAX;
  const strengthRange = expandedStrength
    ? (v3Controls.style_strength.expanded_range ?? v3Controls.style_strength.range)
    : v3Controls.style_strength.range;
  const modePresets = presets.filter((item) => item.mode === mode);

  return (
    <div className="mx-auto w-full max-w-4xl">
      <PageHeader title="Create" />

      <form
        className="grid gap-5"
        onSubmit={form.handleSubmit(async (values) => {
          await create.mutateAsync({
            body: buildCreateGenerationRequest(values, connection.id, projectId),
            idempotencyKey: createIntentKey(),
          });
        })}
      >
        <Panel className="grid gap-6 rounded-none border-0 bg-transparent p-0 shadow-none sm:rounded-lg sm:border sm:bg-surface sm:p-6 sm:shadow-sm lg:p-8">
          <div className="grid gap-5 border-b border-border pb-6 sm:grid-cols-2 lg:grid-cols-3">
            <Field>
              <LabelWithInfo
                htmlFor="composer-project"
                label="Project"
                info="Choose the Song Composer workspace that will own this generation."
                required
              />
              <Select
                id="composer-project"
                options={[
                  { value: 'automatic', label: 'New project for this generation' },
                  ...projects
                    .filter((project) => project.status === 'active')
                    .map((project) => ({ value: project.id, label: project.title })),
                ]}
                placeholder="Select a project"
                value={projectId ?? 'automatic'}
                onValueChange={(nextValue) => {
                  if (nextValue === 'automatic') {
                    setProjectId(null);
                    return;
                  }
                  setProjectId(nextValue);
                }}
              />
            </Field>

            {modePresets.length ? (
              <Field>
                <LabelWithInfo
                  htmlFor="creation-preset"
                  label="Preset"
                  info="Apply a saved starting point without locking any control."
                />
                <Controller
                  control={form.control}
                  name="presetId"
                  render={({ field }) => (
                    <Select
                      id="creation-preset"
                      name={field.name}
                      onBlur={field.onBlur}
                      options={[
                        { value: 'none', label: 'No preset' },
                        ...modePresets.map((preset) => ({
                          value: preset.id,
                          label: preset.name,
                        })),
                      ]}
                      placeholder="No preset"
                      ref={field.ref}
                      value={field.value || 'none'}
                      onValueChange={(id) => {
                        if (id === 'none') {
                          field.onChange('');
                          return;
                        }
                        const preset = modePresets.find((item) => item.id === id);
                        form.reset(valuesFromPreset(preset, capability, mode));
                      }}
                    />
                  )}
                />
              </Field>
            ) : null}

            {capability.modes.length > 1 ? (
              <Controller
                control={form.control}
                name="mode"
                render={({ field }) => (
                  <SegmentedControl
                    label="Mode"
                    value={field.value}
                    onChange={(nextMode) => {
                      field.onChange(nextMode);
                      const selected = presets.find(
                        (item) => item.id === form.getValues('presetId'),
                      );
                      if (selected && selected.mode !== nextMode) form.setValue('presetId', '');
                    }}
                    options={capability.modes.map((item) => ({
                      value: item,
                      label: item === 'advanced' ? 'Advanced' : 'Simple',
                    }))}
                  />
                )}
              />
            ) : null}

            {mode === 'advanced' && controls.model.supported ? (
              <Field>
                <LabelWithInfo
                  htmlFor="creation-model"
                  label="Model"
                  info="Choose the provider model used for this generation."
                />
                <Controller
                  control={form.control}
                  name="modelId"
                  render={({ field }) => (
                    <Select
                      id="creation-model"
                      name={field.name}
                      onBlur={field.onBlur}
                      options={controls.model.options.flatMap((option) =>
                        isModelId(option.id) ? [{ value: option.id, label: option.label }] : [],
                      )}
                      ref={field.ref}
                      value={field.value}
                      onValueChange={(nextModel) => {
                        if (!isModelId(nextModel)) return;
                        field.onChange(nextModel);
                        const selected = presets.find(
                          (item) => item.id === form.getValues('presetId'),
                        );
                        const presetModel = selected?.input_defaults.model_id;
                        if (isModelId(presetModel) && presetModel !== nextModel) {
                          form.setValue('presetId', '');
                        }
                      }}
                    />
                  )}
                />
              </Field>
            ) : null}
          </div>

          {mode === 'simple' ? (
            <Field>
              <LabelWithInfo
                htmlFor="simple-prompt"
                label="Prompt"
                info="Describe the complete song you want to create."
                required
              />
              <Textarea
                id="simple-prompt"
                rows={8}
                maxLength={simplePromptMax}
                placeholder="Describe the song…"
                aria-invalid={Boolean(form.formState.errors.simplePrompt)}
                {...form.register('simplePrompt')}
              />
              <FieldFeedback
                current={simplePrompt.length}
                maximum={simplePromptMax}
                error={form.formState.errors.simplePrompt?.message}
              />
            </Field>
          ) : (
            <div className="grid gap-7">
              <FormSection title="Content">
                <Controller
                  control={form.control}
                  name="lyricsMode"
                  render={({ field }) => (
                    <SegmentedControl
                      label="Lyrics"
                      info="Generate lyrics from an idea, supply exact lyrics, or create an instrumental."
                      value={field.value}
                      onChange={field.onChange}
                      options={controls.lyrics.modes.map((item) => ({
                        value: item,
                        label:
                          item === 'auto' ? 'Auto' : item === 'custom' ? 'Custom' : 'No lyrics',
                      }))}
                    />
                  )}
                />
                {lyricsMode === 'auto' ? (
                  <Field>
                    <LabelWithInfo
                      htmlFor="lyrics-instruction"
                      label="Lyrics idea"
                      info="Describe the subject or story the generated lyrics should follow."
                      required
                    />
                    <Textarea
                      id="lyrics-instruction"
                      rows={5}
                      maxLength={lyricsInstructionMax}
                      placeholder="What should the lyrics be about?"
                      aria-invalid={Boolean(form.formState.errors.lyricsInstruction)}
                      {...form.register('lyricsInstruction')}
                    />
                    <FieldFeedback
                      current={lyricsInstruction.length}
                      maximum={lyricsInstructionMax}
                      error={form.formState.errors.lyricsInstruction?.message}
                    />
                  </Field>
                ) : null}
                {lyricsMode === 'custom' ? (
                  <Field>
                    <LabelWithInfo
                      htmlFor="custom-lyrics"
                      label="Custom lyrics"
                      info="These exact words are sent as the song lyrics."
                      required
                    />
                    <Textarea
                      id="custom-lyrics"
                      rows={10}
                      maxLength={customLyricsMax}
                      placeholder="[Verse]\nWrite or paste your lyrics…"
                      aria-invalid={Boolean(form.formState.errors.customLyrics)}
                      {...form.register('customLyrics')}
                    />
                    <FieldFeedback
                      current={customLyrics.length}
                      maximum={customLyricsMax}
                      error={form.formState.errors.customLyrics?.message}
                    />
                  </Field>
                ) : null}
              </FormSection>

              <FormSection title="Sound">
                <Controller
                  control={form.control}
                  name="styleGuidanceMode"
                  render={({ field }) => (
                    <ToggleField
                      label="Auto tags"
                      info="On lets the connected provider derive tags from your sound direction. Off lets you enter tags directly."
                      checked={field.value === 'auto'}
                      onChange={(checked) => field.onChange(checked ? 'auto' : 'tags')}
                    />
                  )}
                />
                {styleGuidanceMode === 'auto' ? (
                  <Field>
                    <LabelWithInfo
                      htmlFor="style-instruction"
                      label="Sound direction"
                      info="Describe the genre, mood, instrumentation, vocals, and production."
                      required
                    />
                    <Textarea
                      id="style-instruction"
                      rows={6}
                      maxLength={styleInstructionMax}
                      placeholder="Genre, mood, instrumentation, vocals, production…"
                      aria-invalid={Boolean(form.formState.errors.styleInstruction)}
                      {...form.register('styleInstruction')}
                    />
                    <FieldFeedback
                      current={styleInstruction.length}
                      maximum={styleInstructionMax}
                      error={form.formState.errors.styleInstruction?.message}
                    />
                  </Field>
                ) : (
                  <Field>
                    <LabelWithInfo
                      htmlFor="style-tags"
                      label="Style tags"
                      info={`Enter at least ${minimumStyleTagCount(capability, modelId)} tags. Press Enter after each one.`}
                      required
                    />
                    <Controller
                      control={form.control}
                      name="styleTags"
                      render={({ field }) => (
                        <StyleTagInput
                          id="style-tags"
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value);
                            void form.trigger('styleTags');
                          }}
                          maxItems={controls.style_guidance.max_items}
                          validateTag={(tag) =>
                            validateCreationStyleTag(connection.id, {
                              model_id: modelId,
                              tag,
                            })
                          }
                          validationMessage={form.formState.errors.styleTags?.message}
                        />
                      )}
                    />
                  </Field>
                )}

                {modelId === 'v3' && v3Controls.avoidance.supported ? (
                  <>
                    <Controller
                      control={form.control}
                      name="avoidanceEnabled"
                      render={({ field }) => (
                        <ToggleField
                          label="Avoid styles"
                          info="Exclude selected labels from the result."
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    {avoidanceEnabled ? (
                      <Field>
                        <FieldLabel htmlFor="avoidance-tags">Styles to avoid</FieldLabel>
                        <Controller
                          control={form.control}
                          name="avoidanceTags"
                          render={({ field }) => (
                            <StyleTagInput
                              id="avoidance-tags"
                              tone="neutral"
                              value={field.value}
                              placeholder="Type a style to avoid and press Enter"
                              onChange={(value) => {
                                field.onChange(value);
                                void form.trigger('avoidanceTags');
                              }}
                              maxItems={v3Controls.avoidance.max_items}
                              validateTag={(tag) =>
                                validateCreationStyleTag(connection.id, {
                                  model_id: modelId,
                                  tag,
                                })
                              }
                              validationMessage={form.formState.errors.avoidanceTags?.message}
                            />
                          )}
                        />
                      </Field>
                    ) : null}
                  </>
                ) : null}
              </FormSection>

              <FormSection title="Shaping">
                {modelId === 'v3' ? (
                  <>
                    <RangeWithValue
                      form={form}
                      name="styleStrength"
                      range={strengthRange}
                      label="Style strength"
                      info="Controls how strongly the result follows the sound direction or selected tags."
                    />
                    {v3Controls.style_strength.expanded_range ? (
                      <Controller
                        control={form.control}
                        name="expandedStrength"
                        render={({ field }) => (
                          <ToggleField
                            label="Extreme values"
                            info="Expand the available style-strength range."
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    ) : null}

                    {v3Controls.duration.supported ? (
                      <Controller
                        control={form.control}
                        name="durationEnabled"
                        render={({ field }) => (
                          <ToggleField
                            label="Allowed duration range"
                            info="The result may be any length inside these bounds. For example, 0–90 seconds can return a 15- or 28-second track; set both bounds equal to request an exact target."
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    ) : null}
                    {durationEnabled ? (
                      <div className="grid gap-4 sm:grid-cols-2">
                        <Field>
                          <FieldLabel htmlFor="duration-minimum">
                            Shortest allowed (seconds)
                          </FieldLabel>
                          <Input
                            id="duration-minimum"
                            type="number"
                            inputMode="numeric"
                            min={v3Controls.duration.range?.minimum}
                            max={v3Controls.duration.range?.maximum}
                            step={v3Controls.duration.range?.step ?? 30}
                            aria-invalid={Boolean(form.formState.errors.durationMinimum)}
                            {...form.register('durationMinimum')}
                          />
                          <NumericBounds range={v3Controls.duration.range} suffix="seconds" />
                          {form.formState.errors.durationMinimum ? (
                            <FieldError>{form.formState.errors.durationMinimum.message}</FieldError>
                          ) : null}
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="duration-maximum">
                            Longest allowed (seconds)
                          </FieldLabel>
                          <Input
                            id="duration-maximum"
                            type="number"
                            inputMode="numeric"
                            min={v3Controls.duration.range?.minimum}
                            max={v3Controls.duration.range?.maximum}
                            step={v3Controls.duration.range?.step ?? 30}
                            aria-invalid={Boolean(form.formState.errors.durationMaximum)}
                            {...form.register('durationMaximum')}
                          />
                          <NumericBounds range={v3Controls.duration.range} suffix="seconds" />
                          {form.formState.errors.durationMaximum ? (
                            <FieldError>{form.formState.errors.durationMaximum.message}</FieldError>
                          ) : null}
                        </Field>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <>
                    {v22Controls.fancy_mode.supported ? (
                      <Controller
                        control={form.control}
                        name="fancyMode"
                        render={({ field }) => (
                          <ToggleField
                            label="Fancy mode"
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    ) : null}
                    <Controller
                      control={form.control}
                      name="moreAdvanced"
                      render={({ field }) => (
                        <DisclosureField
                          controls="v22-more-advanced-controls"
                          label="More advanced"
                          info="Show the v2.2 fine-tuning controls."
                          expanded={field.value}
                          onChange={() => field.onChange(!field.value)}
                        />
                      )}
                    />
                    {moreAdvanced ? (
                      <div
                        id="v22-more-advanced-controls"
                        className="grid gap-5 border-l border-border pl-4 sm:pl-5"
                      >
                        <RangeWithValue
                          form={form}
                          name="lyricsStyleStrength"
                          range={v22Controls.lyrics_style_strength.range}
                          label="Lyrics & style strength"
                          info="Controls how strongly v2.2 follows the lyrics and style direction."
                        />
                        <RangeWithValue
                          form={form}
                          name="balance"
                          range={v22Controls.balance.range}
                          label="Balance"
                          info="Adjusts the v2.2 generation balance."
                        />
                        {v22Controls.bpm.supported ? (
                          <Controller
                            control={form.control}
                            name="bpmMode"
                            render={({ field }) => (
                              <SegmentedControl
                                label="BPM"
                                value={field.value}
                                onChange={field.onChange}
                                options={v22Controls.bpm.modes.map((item) => ({
                                  value: item,
                                  label:
                                    item === 'disabled'
                                      ? 'Disabled'
                                      : item === 'auto'
                                        ? 'Auto'
                                        : 'Manual',
                                }))}
                              />
                            )}
                          />
                        ) : null}
                        {bpmMode === 'manual' ? (
                          <Field className="max-w-64">
                            <FieldLabel htmlFor="beats-per-minute">Beats per minute</FieldLabel>
                            <Input
                              id="beats-per-minute"
                              type="number"
                              inputMode="numeric"
                              min={v22Controls.bpm.manual_range?.minimum}
                              max={v22Controls.bpm.manual_range?.maximum}
                              step={v22Controls.bpm.manual_range?.step ?? 1}
                              aria-invalid={Boolean(form.formState.errors.beatsPerMinute)}
                              {...form.register('beatsPerMinute')}
                            />
                            <NumericBounds range={v22Controls.bpm.manual_range} suffix="BPM" />
                            {form.formState.errors.beatsPerMinute ? (
                              <FieldError>
                                {form.formState.errors.beatsPerMinute.message}
                              </FieldError>
                            ) : null}
                          </Field>
                        ) : null}
                        {v22Controls.boost_style.supported ? (
                          <Controller
                            control={form.control}
                            name="boostStyle"
                            render={({ field }) => (
                              <ToggleField
                                label="Boost style"
                                checked={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </>
                )}
              </FormSection>
            </div>
          )}

          <div className="flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:justify-end">
            <Button
              className="w-full sm:w-auto"
              type="button"
              variant="secondary"
              disabled={savePreset.isPending || connection.status !== 'active'}
              onClick={() => {
                void form.handleSubmit((values) => {
                  setPresetName('');
                  setPresetDescription('');
                  setSavedPresetName(null);
                  savePreset.reset();
                  setPresetDraft(buildGenerationInput(values));
                })();
              }}
            >
              <BookmarkPlus aria-hidden="true" />
              Save preset
            </Button>
            <Button
              className="w-full sm:w-auto"
              type="submit"
              disabled={create.isPending || connection.status !== 'active'}
            >
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
          {create.error ? (
            <FieldError>Creation failed. Check the settings and try again.</FieldError>
          ) : null}
          {savedPresetName ? (
            <p role="status" className="text-sm font-medium text-ok">
              Saved preset “{savedPresetName}”.
            </p>
          ) : null}
        </Panel>
      </form>

      <Dialog
        open={presetDraft !== null}
        onOpenChange={(open) => {
          if (!open && !savePreset.isPending) setPresetDraft(null);
        }}
      >
        <DialogContent>
          <DialogTitle>Save creation preset</DialogTitle>
          <DialogDescription>
            Save these settings for reuse. One-off changes do not update the preset.
          </DialogDescription>
          <form
            className="grid gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              if (!presetDraft || !presetName.trim()) return;
              void savePreset
                .mutateAsync({
                  body: {
                    connection_id: connection.id,
                    name: presetName.trim(),
                    description: presetDescription.trim() || null,
                    input: presetDraft,
                  },
                  idempotencyKey: createIntentKey(),
                })
                .then((preset) => {
                  form.setValue('presetId', preset.id);
                  setSavedPresetName(preset.name);
                  setPresetDraft(null);
                });
            }}
          >
            <Field>
              <FieldLabel htmlFor="preset-name">Name</FieldLabel>
              <Input
                id="preset-name"
                autoFocus
                required
                maxLength={256}
                value={presetName}
                onChange={(event) => setPresetName(event.target.value)}
                placeholder="Late-night bounce"
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="preset-description">Description</FieldLabel>
              <Textarea
                id="preset-description"
                rows={3}
                maxLength={1_000}
                value={presetDescription}
                onChange={(event) => setPresetDescription(event.target.value)}
                placeholder="Optional note about when to use this preset"
              />
            </Field>
            {savePreset.error ? (
              <FieldError>Preset could not be saved. Check the name and try again.</FieldError>
            ) : null}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="secondary"
                disabled={savePreset.isPending}
                onClick={() => setPresetDraft(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={savePreset.isPending || !presetName.trim()}>
                {savePreset.isPending ? 'Saving…' : 'Save preset'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormSection({ children, title }: { children: ReactNode; title: string }) {
  return (
    <section className="grid gap-5 border-b border-border pb-7 last:border-b-0 last:pb-0">
      <h2 className="text-xl font-semibold tracking-[-0.025em] text-ink">{title}</h2>
      {children}
    </section>
  );
}

function FieldFeedback({
  current,
  error,
  maximum,
}: {
  current: number;
  error?: ReactNode;
  maximum: number;
}) {
  return (
    <div
      className={cn('flex min-h-5 items-start gap-3', error ? 'justify-between' : 'justify-end')}
    >
      {error ? <FieldError>{error}</FieldError> : null}
      <span
        aria-label={`${current} of ${maximum} characters`}
        className="shrink-0 font-mono text-xs tabular-nums text-ink-3"
      >
        {current.toLocaleString()} / {maximum.toLocaleString()}
      </span>
    </div>
  );
}

function NumericBounds({ range, suffix }: { range: NumericRange | null; suffix: string }) {
  if (!range) return null;
  return (
    <span className="justify-self-end font-mono text-xs tabular-nums text-ink-3">
      {range.minimum}–{range.maximum} {suffix}
    </span>
  );
}

function RangeWithValue({
  form,
  info,
  label,
  name,
  range,
}: {
  form: UseFormReturn<CreationFields>;
  info: ReactNode;
  label: string;
  name: 'styleStrength' | 'lyricsStyleStrength' | 'balance';
  range: NumericRange | null;
}) {
  const id = name.replaceAll(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
  const error = form.formState.errors[name];
  return (
    <Field>
      <div className="flex items-center justify-between gap-3">
        <LabelWithInfo htmlFor={`${id}-range`} label={label} info={info} required />
        {range ? (
          <span className="font-mono text-xs tabular-nums text-ink-3">
            {range.minimum}–{range.maximum}
          </span>
        ) : null}
      </div>
      <Controller
        control={form.control}
        name={name}
        render={({ field }) => (
          <div className="grid grid-cols-[minmax(0,1fr)_5.5rem] items-center gap-3 sm:gap-4">
            <input
              id={`${id}-range`}
              aria-label={label}
              className="h-11 w-full cursor-pointer accent-[var(--accent)]"
              type="range"
              min={range?.minimum ?? 0}
              max={range?.maximum ?? 1}
              step={range?.step ?? 0.01}
              value={field.value}
              onChange={field.onChange}
            />
            <Input
              id={id}
              aria-label={`${label} value`}
              className="font-mono tabular-nums"
              type="number"
              inputMode="decimal"
              min={range?.minimum}
              max={range?.maximum}
              step={range?.step ?? 'any'}
              value={field.value}
              onBlur={field.onBlur}
              onChange={field.onChange}
              aria-invalid={Boolean(error)}
            />
          </div>
        )}
      />
      {error?.message ? <FieldError>{error.message}</FieldError> : null}
    </Field>
  );
}

function DisclosureField({
  controls,
  expanded,
  info,
  label,
  onChange,
}: {
  controls: string;
  expanded: boolean;
  info?: ReactNode;
  label: string;
  onChange: () => void;
}) {
  return (
    <div className="flex min-h-12 items-center justify-between gap-4 rounded-sm border border-border bg-bg px-4 py-2.5 text-base font-medium text-ink">
      <span className="inline-flex items-center gap-1">
        {label}
        {info ? <InfoTooltip label={`About ${label}`}>{info}</InfoTooltip> : null}
      </span>
      <button
        type="button"
        aria-controls={controls}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Hide' : 'Show'} ${label.toLowerCase()} controls`}
        onClick={onChange}
        className="grid size-11 shrink-0 place-items-center rounded-full text-ink-2 outline-none transition focus-visible:ring-2 focus-visible:ring-accent-ring"
      >
        <ChevronDown
          aria-hidden="true"
          className={cn('size-5 transition-transform', expanded && 'rotate-180')}
        />
      </button>
    </div>
  );
}

function LabelWithInfo({
  htmlFor,
  info,
  label,
  required = false,
}: {
  htmlFor?: string;
  info: ReactNode;
  label: string;
  required?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <FieldLabel htmlFor={htmlFor}>
        {label}
        {required ? <span className="sr-only"> (required)</span> : null}
      </FieldLabel>
      <InfoTooltip label={`About ${label}`}>{info}</InfoTooltip>
    </div>
  );
}

function ToggleField({
  checked,
  info,
  label,
  onChange,
}: {
  checked: boolean;
  info?: ReactNode;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  const labelId = `toggle-${label.replaceAll(' ', '-').toLowerCase()}`;
  return (
    <div className="flex min-h-12 items-center justify-between gap-4 rounded-sm border border-border bg-bg px-4 py-2.5 text-base font-medium text-ink">
      <span id={labelId} className="inline-flex items-center gap-1">
        {label}
        {info ? <InfoTooltip label={`About ${label}`}>{info}</InfoTooltip> : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-labelledby={labelId}
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className="relative grid size-11 shrink-0 place-items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
      >
        <span
          aria-hidden="true"
          className={cn(
            'absolute h-6 w-11 rounded-full border transition',
            checked ? 'border-accent bg-accent' : 'border-border-hi bg-surface-hi',
          )}
        />
        <span
          aria-hidden="true"
          className={cn(
            'absolute size-5 rounded-full shadow transition',
            checked ? 'translate-x-[0.6rem] bg-accent-ink' : '-translate-x-[0.6rem] bg-ink',
          )}
        />
      </button>
    </div>
  );
}
